import { Loader2 } from 'lucide-react'

import { Skeleton } from '@/components/ui'
import { cn } from '@/utils'

export interface SpinnerProps {
  className?: string
  label?: string
}

export function Spinner({ className, label = 'Carregando' }: SpinnerProps) {
  return (
    <Loader2
      role="status"
      aria-label={label}
      className={cn('text-muted-foreground size-5 animate-spin', className)}
    />
  )
}

/**
 * Route-level fallback for `Suspense`. Sized to occupy the content area so the
 * shell does not collapse while a lazy chunk downloads.
 */
export function PageLoader({ label = 'Carregando página' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-3"
    >
      <Spinner className="size-8" label={label} />
      <p className="text-muted-foreground text-sm">{label}…</p>
    </div>
  )
}

/** Blocks interaction during a mutation without unmounting the content below. */
export function LoadingOverlay({
  visible,
  label = 'Processando',
}: {
  visible: boolean
  label?: string
}) {
  if (!visible) return null

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-background/70 absolute inset-0 z-40 flex flex-col items-center justify-center gap-2 backdrop-blur-sm"
    >
      <Spinner className="size-6" label={label} />
      <p className="text-muted-foreground text-sm">{label}…</p>
    </div>
  )
}

export function TableSkeleton({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-2" aria-hidden>
      <Skeleton className="h-10 w-full" />
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex gap-2">
          {Array.from({ length: columns }, (_, column) => (
            <Skeleton key={column} className="h-12 flex-1" />
          ))}
        </div>
      ))}
    </div>
  )
}

export function CardListSkeleton({ items = 6 }: { items?: number }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-hidden>
      {Array.from({ length: items }, (_, index) => (
        <div key={index} className="space-y-3 rounded-xl border p-4">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-9 w-28" />
        </div>
      ))}
    </div>
  )
}
